/**
 * StatusBadge Component
 * 
 * Reusable status badge component for displaying status across the application
 * Supports Users (active/inactive/blocked), Groups (active/inactive), 
 * and Certificates (valid/expiring/expired)
 * 
 * @component
 * @example
 * // User status
 * <StatusBadge status="active" type="user" />
 * 
 * // Certificate status
 * <StatusBadge status="expiring" type="certificate" />
 * 
 * // Group status
 * <StatusBadge status="active" type="group" />
 */

import { FC } from 'react';
import { FiCheckCircle, FiMinusCircle, FiXCircle, FiAlertCircle, FiClock, FiPlus } from 'react-icons/fi';
import type { StatusBadgeProps, StatusConfig } from './StatusBadge.types';
import styles from './StatusBadge.module.css';

/**
 * Status configuration map for all entity types
 * Maps: type:status -> { label, icon, className }
 */
const STATUS_CONFIG_MAP: Record<string, Record<string, StatusConfig>> = {
  // User statuses
  user: {
    active: {
      label: 'Ativo',
      icon: <FiCheckCircle size={14} style={{ flexShrink: 0 }} />,
      className: styles.statusActive,
    },
    inactive: {
      label: 'Inativo',
      icon: <FiMinusCircle size={14} style={{ flexShrink: 0 }} />,
      className: styles.statusInactive,
    },
    blocked: {
      label: 'Bloqueado',
      icon: <FiXCircle size={14} style={{ flexShrink: 0 }} />,
      className: styles.statusBlocked,
    },
  },
  // Group statuses
  group: {
    active: {
      label: 'Ativo',
      icon: <FiCheckCircle size={14} style={{ flexShrink: 0 }} />,
      className: styles.statusActive,
    },
    inactive: {
      label: 'Inativo',
      icon: <FiMinusCircle size={14} style={{ flexShrink: 0 }} />,
      className: styles.statusInactive,
    },
  },
  // Certificate statuses
  certificate: {
    valid: {
      label: 'Válido',
      icon: <FiCheckCircle size={14} style={{ flexShrink: 0 }} />,
      className: styles.statusValid,
    },
    expiring: {
      label: 'Expirando',
      icon: <FiAlertCircle size={14} style={{ flexShrink: 0 }} />,
      className: styles.statusExpiring,
    },
    expired: {
      label: 'Expirado',
      icon: <FiXCircle size={14} style={{ flexShrink: 0 }} />,
      className: styles.statusExpired,
    },
  },
  // Event statuses
  event: {
    PENDING: {
      label: 'Pendente',
      icon: <FiClock size={14} style={{ flexShrink: 0 }} />,
      className: styles.statusPending,
    },
    PROCESSED: {
      label: 'Processado',
      icon: <FiCheckCircle size={14} style={{ flexShrink: 0 }} />,
      className: styles.statusProcessed,
    },
    STUCK: {
      label: 'Travado',
      icon: <FiXCircle size={14} style={{ flexShrink: 0 }} />,
      className: styles.statusStuck,
    },
  },
  // Event types
  eventType: {
    CERTIFICATE_ADDED: {
      label: 'Certificado Adicionado',
      icon: <FiPlus size={14} style={{ flexShrink: 0 }} />,
      className: styles.statusEventAdded,
    },
    CERTIFICATE_REMOVED: {
      label: 'Certificado Removido',
      icon: <FiMinusCircle size={14} style={{ flexShrink: 0 }} />,
      className: styles.statusEventRemoved,
    },
  },
};

/**
 * Get status configuration from centralized map
 * Falls back to inactive status if not found
 */
const getStatusConfig = (type: string, status: string): StatusConfig => {
  const typeConfig = STATUS_CONFIG_MAP[type];

  if (!typeConfig) {
    // Fallback: type not found
    return {
      label: status,
      icon: <FiMinusCircle size={14} style={{ flexShrink: 0 }} />,
      className: styles.statusInactive,
    };
  }

  return (
    typeConfig[status] || {
      label: status,
      icon: <FiMinusCircle size={14} style={{ flexShrink: 0 }} />,
      className: styles.statusInactive,
    }
  );
};

/**
 * StatusBadge Component
 * 
 * Renders a styled status badge with icon and label
 */
const StatusBadge: FC<StatusBadgeProps> = ({
  status,
  type,
  className = '',
  ariaLabel,
}) => {
  const config = getStatusConfig(type, status);

  return (
    <span
      className={`${styles.statusBadge} ${config.className} ${className}`}
      role="status"
      aria-label={ariaLabel || `Status: ${config.label}`}
    >
      {config.icon}
      {config.label}
    </span>
  );
};

export default StatusBadge;
